import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { App } from '@capacitor/app';
import { Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { AppIdentityService } from './app-identity.service';

/*
  ╔══════════════════════════════════════════════════════════════════════╗
  ║                   OTA UPDATE SERVICE - MİMARİ NOTU                 ║
  ╠══════════════════════════════════════════════════════════════════════╣
  ║                                                                    ║
  ║  Strateji: Manuel kontrol, Zorunlu Güncelleme                      ║
  ║                                                                    ║
  ║  Web (Tarayıcı):                                                   ║
  ║    - Bu servis web'de HİÇBİR şey yapmaz.                          ║
  ║    - Capacitor.isNativePlatform() kontrolü her metodda.            ║
  ║                                                                    ║
  ║  Mobil (Capacitor/Android):                                        ║
  ║    1. App açılır → initialize() → notifyAppReady() [ZORUNLU]       ║
  ║       (Bu çağrılmazsa updater otomatik rollback yapar)             ║
  ║    2. Login sonrası → checkForUpdate() → backend'e sorgu           ║
  ║    3. Güncelleme varsa → updateRequired Subject emit eder          ║
  ║    4. OtaUpdateModalComponent modalı açar (kapatılamaz)            ║
  ║    5. Kullanıcı "Güncelle" → downloadAndApply() → reload           ║
  ║                                                                    ║
  ║  Backend endpoint: GET {baseUrl}/api/v1/updates/check              ║
  ║    Query params: version, platform, build, appId                   ║
  ║    Response (update): { version: string, url: string }             ║
  ║    Response (up-to-date): { version: string }                      ║
  ║                                                                    ║
  ╚══════════════════════════════════════════════════════════════════════╝
*/

export interface OtaUpdateInfo {
  version: string;
  url: string;
  checksum?: string;
  isStoreUpdate?: boolean;
}

@Injectable({ providedIn: 'root' })
export class OtaUpdateService {

  /**
   * Zorunlu güncelleme sinyali.
   * AppComponent dinler → OtaUpdateModalComponent'i açar.
   */
  public updateRequired = new Subject<OtaUpdateInfo>();

  /**
   * İndirme progress bilgisi (0-100).
   * OtaUpdateModalComponent dinler → progress bar günceller.
   */
  public downloadProgress = new Subject<number>();

  constructor(private appIdentity: AppIdentityService) { }

  // ─────────────────────────────────────────
  // Başlatma (App başlangıcında çağrılır)
  // ─────────────────────────────────────────

  /**
   * OTA servisini başlatır.
   * ZORUNLU: notifyAppReady() çağrılmazsa updater rollback yapar.
   * AppComponent.ngOnInit() içinde pushNotificationService.initialize() ile birlikte çağrılır.
   */
  async initialize(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return; // Web'de OTA çalışmaz
    }

    try {
      // KRITIK: Updater'a "uygulama başarıyla başladı" sinyali ver.
      // Bu çağrılmazsa @capgo/capacitor-updater 10 saniye sonra önceki versiyona rollback yapar.
      await CapacitorUpdater.notifyAppReady();
      console.log('[OTA] App ready — notifyAppReady() called.');

      // Güncelleme hatası listener
      await CapacitorUpdater.addListener('updateFailed', (info) => {
        console.error('[OTA] Update failed:', info);
      });

      // Download progress listener
      await CapacitorUpdater.addListener('download', (info) => {
        console.log(`[OTA] Download progress: ${info.percent}%`);
        this.downloadProgress.next(info.percent);
      });

    } catch (err) {
      // notifyAppReady başarısız olursa uygulamayı durdurmaz; sadece logla
      console.error('[OTA] initialize() error:', err);
    }
  }

  // ─────────────────────────────────────────
  // Güncelleme Kontrolü
  // ─────────────────────────────────────────

  /**
   * Backend'e sürüm sorgusu yapar.
   * Güncelleme varsa updateRequired Subject'ini emit eder.
   * Login sonrası (dashboard ngOnInit içinde registerCachedToken ile birlikte) çağrılır.
   */
  async checkForUpdate(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      // OTA versiyonu (Web bundle versiyonu)
      const currentVersion = environment.appVersion || '1.0.0';
      
      // Native versiyonu (Play Store)
      let buildNumber = '0';
      try {
        const appInfo = await App.getInfo();
        buildNumber = appInfo.build; 
      } catch (e) {
        console.warn('[OTA] App.getInfo() failed, using fallback build number.');
      }

      const platform = Capacitor.getPlatform();

      // Hangi kabuk çalışıyor: com.demoemre.app mı, com.sagel.app mı. İki
      // container iki ayrı Play hesabının listeleme bilgisini tutuyor; backend
      // /check isteğini bu değere göre doğru olana yönlendirir (V109).
      // Kimlik bilinmiyorsa parametre hiç gönderilmez — backend'de "appId yok"
      // eski uygulama demektir, ki eski sürümler bu alanı zaten göndermiyor.
      const appId = await this.appIdentity.getAppId();
      const appIdParam = appId ? `&appId=${encodeURIComponent(appId)}` : '';

      const url = `${environment.baseUrl}/api/v1/updates/check?version=${currentVersion}&build=${buildNumber}&platform=${platform}${appIdParam}`;

      console.log(`[OTA] Checking for update... Version: ${currentVersion}, Build: ${buildNumber}, Platform: ${platform}, App: ${appId || 'unknown'}`);

      const response = await fetch(url);

      if (!response.ok) {
        console.warn(`[OTA] Update check HTTP error: ${response.status}`);
        return;
      }

      const data: { version?: string; url?: string; checksum?: string; isStoreUpdate?: boolean } = await response.json();

      console.log('[OTA] Update check response:', data);

      // Güncelleme var mı?
      // Backend güncelleme yokken clientVersion'ı döner (url alanı olmaz)
      if (data.url && (data.version !== currentVersion || data.isStoreUpdate)) {
        console.log(`[OTA] Update available: ${currentVersion} → ${data.version} (Store: ${!!data.isStoreUpdate})`);
        this.updateRequired.next({ 
          version: data.version || 'Update', 
          url: data.url, 
          checksum: data.checksum,
          isStoreUpdate: !!data.isStoreUpdate
        });
      } else {
        console.log('[OTA] App is up to date.');
      }

    } catch (err) {
      // Ağ hatası vb. — uygulamayı durdurmaz
      console.warn('[OTA] checkForUpdate failed (network issue?):', err);
    }
  }

  // ─────────────────────────────────────────
  // İndirme ve Uygulama
  // ─────────────────────────────────────────

  /**
   * ZIP'i indirir ve uygular. Uygulama bu çağrıdan sonra yeniden başlar.
   * OtaUpdateModalComponent tarafından çağrılır.
   *
   * Hata nesnelerine `_phase: 'download' | 'set'` eklenir — modal
   * hangi aşamada hata oluştuğunu `buildErrorDetail()` ile raporlar.
   *
   * @throws Indirme veya uygulama başarısız olursa Error fırlatır.
   */
  async downloadAndApply(updateInfo: OtaUpdateInfo, retries = 1): Promise<void> {
    console.log(`[OTA] Downloading v${updateInfo.version} from ${updateInfo.url} (Retries left: ${retries})`);

    // ── Aşama 1: İndirme ─────────────────────────────────────────────────────
    // checksum varsa bütünlük doğrulaması @capgo/capacitor-updater içinde yapılır;
    // yanlış checksum → download Error fırlatır, bundle uygulanmaz.
    let bundle: any;
    try {

      bundle = await CapacitorUpdater.download({
        url: updateInfo.url,
        version: updateInfo.version,
        checksum: updateInfo.checksum,
      });
    } catch (err: any) {
      // Geçici ağ kopmaları için otomatik tekrar deneme (Auto-Retry)
      if (retries > 0) {
        console.warn(`[OTA] Download failed, retrying in 2 seconds... (${retries} retries left)`);
        await new Promise(resolve => setTimeout(resolve, 2000));
        return this.downloadAndApply(updateInfo, retries - 1);
      }
      
      err._phase = 'download';
      throw err;
    }

    console.log('[OTA] Download complete. Applying bundle...');

    // ── Aşama 2: Uygulama ────────────────────────────────────────────────────
    // Bundle'ı etkinleştir → uygulama bu noktadan sonra yeniden yüklenir.
    // Not: set() sonrası kod çalışmaz (uygulama reload olur).
    // Hata olursa CapacitorUpdater.addListener('updateFailed') de tetiklenir.
    try {
      await CapacitorUpdater.set(bundle);
    } catch (err: any) {
      err._phase = 'set';
      throw err;
    }
  }
}
